import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ProjectPerformance = ({ projectId }) => {
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPerformance();
  }, [projectId]);

  const fetchPerformance = async () => {
    try {
      const response = await api.get(`/analytics/project-performance/${projectId}`);
      setPerformance(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching performance:', error);
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading performance data...</div>;

  if (!performance || performance.floors.length === 0) {
    return (
      <div className="card">
        <p>No budget or performance data available yet. Set up budgets first.</p>
      </div>
    );
  }

  const getVarianceColor = (variance) => {
    if (variance > 5) return '#e74c3c'; // Red - over budget
    if (variance < -5) return '#27ae60'; // Green - under budget
    return '#f39c12'; // Orange - close to budget
  };

  return (
    <div className="project-performance">
      {/* PROJECT TOTALS */}
      <div className="card" style={{ marginBottom: '30px', background: '#34495e', color: 'white' }}>
        <h2>Project Totals</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '20px', marginTop: '20px' }}>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Budgeted Manhours</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
              {performance.totals.budgeted_manhours.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Actual Manhours</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
              {performance.totals.actual_manhours.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>MH Variance</div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: 'bold',
              color: performance.totals.variance_manhours > 0 ? '#e74c3c' : '#27ae60'
            }}>
              {performance.totals.variance_manhours > 0 ? '+' : ''}
              {performance.totals.variance_manhours.toLocaleString()}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Budgeted CY</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
              {performance.floors.reduce((sum, f) => 
                sum + f.elements.reduce((s, e) => s + (e.budgeted_cubic_yards || 0), 0), 0
              ).toFixed(1)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>Actual CY</div>
            <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
              {performance.floors.reduce((sum, f) => 
                sum + f.elements.reduce((s, e) => s + e.actual_cubic_yards, 0), 0
              ).toFixed(1)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: '14px', opacity: 0.8 }}>% MH Variance</div>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: 'bold',
              color: performance.totals.variance_percent > 0 ? '#e74c3c' : '#27ae60'
            }}>
              {performance.totals.variance_percent > 0 ? '+' : ''}
              {performance.totals.variance_percent}%
            </div>
          </div>
        </div>
      </div>

      {/* FLOORS BREAKDOWN */}
      {performance.floors.map((floor, idx) => (
        <div key={idx} className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>Floor: {floor.floor_name}</h3>
            <div style={{ 
              padding: '8px 16px', 
              borderRadius: '5px',
              background: getVarianceColor(floor.floor_totals.variance_percent),
              color: 'white',
              fontWeight: 'bold'
            }}>
              {floor.floor_totals.variance_percent > 0 ? '+' : ''}
              {floor.floor_totals.variance_percent}% variance
            </div>
          </div>

          {/* Floor Totals */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(5, 1fr)', 
            gap: '15px',
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '5px',
            marginBottom: '15px'
          }}>
            <div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Budget MH</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {floor.floor_totals.budgeted_manhours}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Actual MH</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {floor.floor_totals.actual_manhours}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>MH Variance</div>
              <div style={{ 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: floor.floor_totals.variance_manhours > 0 ? '#e74c3c' : '#27ae60'
              }}>
                {floor.floor_totals.variance_manhours > 0 ? '+' : ''}
                {floor.floor_totals.variance_manhours}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Budget CY</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {floor.elements.reduce((sum, el) => sum + (el.budgeted_cubic_yards || 0), 0).toFixed(1)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Actual CY</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {floor.elements.reduce((sum, el) => sum + el.actual_cubic_yards, 0).toFixed(1)}
              </div>
            </div>
          </div>

          {/* Elements Table */}
          <table>
            <thead>
              <tr>
                <th>Element</th>
                <th>Budget MH</th>
                <th>Actual MH</th>
                <th>MH Variance</th>
                <th>Budget CY</th>
                <th>Actual CY</th>
                <th>CY Variance</th>
                <th>Budget MH/CY</th>
                <th>Actual MH/CY</th>
                <th>% Complete</th>
              </tr>
            </thead>
            <tbody>
              {floor.elements.map((element, elementIdx) => {
                const budgetMHCY = element.budgeted_cubic_yards > 0 
                  ? (element.budget.total / element.budgeted_cubic_yards).toFixed(2) 
                  : '-';
                const actualMHCY = element.actual_cubic_yards > 0 
                  ? (element.actual.total / element.actual_cubic_yards).toFixed(2) 
                  : '-';
                const cyVariance = element.budgeted_cubic_yards 
                  ? (element.actual_cubic_yards - element.budgeted_cubic_yards).toFixed(1)
                  : '-';
                
                return (
                  <tr key={elementIdx}>
                    <td>
                      <div style={{ fontWeight: 'bold' }}>{element.element_detail}</div>
                      <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{element.element_category}</div>
                    </td>
                    <td>{element.budget.total}</td>
                    <td>{element.actual.total}</td>
                    <td style={{ 
                      color: element.variance.total > 0 ? '#e74c3c' : '#27ae60',
                      fontWeight: 'bold'
                    }}>
                      {element.variance.total > 0 ? '+' : ''}{element.variance.total}
                      <div style={{ fontSize: '11px' }}>
                        ({element.variance_percent > 0 ? '+' : ''}{element.variance_percent}%)
                      </div>
                    </td>
                    <td>{element.budgeted_cubic_yards || '-'}</td>
                    <td>{element.actual_cubic_yards}</td>
                    <td style={{ 
                      color: cyVariance !== '-' && parseFloat(cyVariance) > 0 ? '#e74c3c' : '#27ae60',
                      fontWeight: 'bold'
                    }}>
                      {cyVariance !== '-' ? `${parseFloat(cyVariance) > 0 ? '+' : ''}${cyVariance}` : '-'}
                    </td>
                    <td>{budgetMHCY}</td>
                    <td style={{
                      color: budgetMHCY !== '-' && actualMHCY !== '-' && parseFloat(actualMHCY) > parseFloat(budgetMHCY) ? '#e74c3c' : '#27ae60',
                      fontWeight: 'bold'
                    }}>
                      {actualMHCY}
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ 
                          flex: 1, 
                          height: '8px', 
                          background: '#ecf0f1', 
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            width: `${Math.min(element.percent_complete, 100)}%`,
                            height: '100%',
                            background: element.percent_complete > 100 ? '#e74c3c' : '#3498db',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                        <span style={{ 
                          minWidth: '45px',
                          fontWeight: 'bold',
                          color: element.percent_complete > 100 ? '#e74c3c' : '#2c3e50'
                        }}>
                          {element.percent_complete}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

export default ProjectPerformance;